import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import WidgetPageShell from '@components/common/WidgetPageShell';
import WidgetNav from '@components/common/WidgetNav';
import mockPerformanceDividend from '@data/mockPerformanceDividend';
import {
  mapDividendDatesApiToBlock,
  mapDividendInfoApiToBlock,
} from '@apis/dividendMappers';
import {
  getApiParamErrorNotificationMessage,
  shouldNotifyDataRequestError,
} from '@apis/client';
import { useDividendInfo } from '@hooks/useDividendInfo';
import { useDividendDates } from '@hooks/useDividendDates';
import { notify } from '@utils/notify';
import { getIframeQueryParamsFromSearch } from '@utils/queryParams';
import DividendInfoGrid from './components/DividendInfoGrid';
import DividendTrendSection from './components/DividendTrendSection';
import './PerformanceDividendPage.less';

/**
 * Performance Dividend screen (route /dividends).
 * Web container 3132:2920 (626px), mobile container 3158:8618 (375px).
 * Layout: nav → info/schedule row → dividend trends section (with footer inside).
 * Info + related schedule are API-backed; trends fetches on its own selection.
 */
const PerformanceDividendPage: React.FC = () => {
  const data = mockPerformanceDividend;
  const { search } = useLocation();
  const { ticker, mkt } = getIframeQueryParamsFromSearch(search);

  const infoQuery = useDividendInfo();
  const datesQuery = useDividendDates();

  const dividendInfo = useMemo(
    () => mapDividendInfoApiToBlock(infoQuery.data, data.dividendInfo),
    [infoQuery.data, data.dividendInfo],
  );

  const relatedSchedule = useMemo(
    () => mapDividendDatesApiToBlock(datesQuery.data, data.relatedSchedule),
    [datesQuery.data, data.relatedSchedule],
  );

  useEffect(() => {
    if (infoQuery.isError && shouldNotifyDataRequestError(infoQuery.error)) {
      notify({
        level: 'error',
        message:
          getApiParamErrorNotificationMessage(infoQuery.error) ??
          '配当情報の取得に失敗しました。',
      });
    }
  }, [infoQuery.isError, infoQuery.error]);

  useEffect(() => {
    if (datesQuery.isError && shouldNotifyDataRequestError(datesQuery.error)) {
      notify({
        level: 'error',
        message:
          getApiParamErrorNotificationMessage(datesQuery.error) ??
          '関連スケジュールの取得に失敗しました。',
      });
    }
  }, [datesQuery.isError, datesQuery.error]);

  return (
    <WidgetPageShell className="performance-dividend">
      <WidgetNav />
      <main className="performance-dividend__body">
        {/* PD03: dividend info + related schedule two-column row */}
        <DividendInfoGrid
          dividendInfo={dividendInfo}
          relatedSchedule={relatedSchedule}
        />
        {/* PD08: full dividend trends section (controls, chart, legend, tables, footer) */}
        <DividendTrendSection data={data.trends} currency={infoQuery.data?.currency} market={mkt} ticker={ticker} />
      </main>
    </WidgetPageShell>
  );
};

export default PerformanceDividendPage;
